import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { siteConfig } from "../config/siteConfig";
import { MessageCircle, ExternalLink, Mail } from "lucide-react";

export default function BookingModal({ open, onOpenChange }) {
  const { bookingChannels, bookingLinks, contact, whatsappDefaultMessage } =
    siteConfig;

  const options = [
    {
      show: bookingChannels.showWhatsApp,
      key: "whatsapp",
      label: "WhatsApp",
      desc: "Chat with us instantly to check availability.",
      href: `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(
        whatsappDefaultMessage
      )}`,
      icon: MessageCircle,
      tone: "green",
    },
    {
      show: bookingChannels.showBookingCom,
      key: "bookingcom",
      label: "Booking.com",
      desc: "Reserve through our trusted partner.",
      href: bookingLinks.bookingCom,
      icon: ExternalLink,
      tone: "dark",
    },
    {
      show: bookingChannels.showAgoda,
      key: "agoda",
      label: "Agoda",
      desc: "Find great deals on your stay.",
      href: bookingLinks.agoda,
      icon: ExternalLink,
      tone: "dark",
    },
    {
      show: bookingChannels.showInquiry,
      key: "inquiry",
      label: "Direct Inquiry",
      desc: "Send us your details — we'll get back shortly.",
      href: "#contact",
      icon: Mail,
      tone: "gold",
      isAnchor: true,
    },
  ].filter((o) => o.show);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="bg-[#FAF9F6] border-0 sm:max-w-lg rounded-3xl p-0 overflow-hidden"
      >
        <div className="p-7 sm:p-10">
          <DialogHeader>
            <p className="font-overline mb-3 text-[#C5A059]">Reserve Your Stay</p>
            <DialogTitle className="font-display text-3xl sm:text-4xl text-[#1A362D] font-light leading-tight">
              How would you like to book?
            </DialogTitle>
            <DialogDescription className="text-[#5C4033] text-sm mt-2">
              Choose your preferred channel — we'll be in touch right away.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-7 space-y-3">
            {options.map((o) => {
              const Icon = o.icon;
              const base =
                "group flex items-center gap-4 w-full p-4 rounded-2xl transition-all cursor-pointer border";
              const tone =
                o.tone === "green"
                  ? "border-[#EBE5D9] hover:border-[#1A362D] hover:bg-[#1A362D] hover:text-[#FAF9F6]"
                  : o.tone === "gold"
                  ? "border-[#EBE5D9] hover:border-[#C5A059] hover:bg-[#C5A059] hover:text-[#1A362D]"
                  : "border-[#EBE5D9] hover:border-[#1A362D] hover:bg-[#F3EFE6]";
              const onClick = () => {
                if (o.isAnchor) {
                  onOpenChange(false);
                  setTimeout(() => {
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }, 200);
                } else {
                  window.open(o.href, "_blank");
                }
              };
              return (
                <button
                  key={o.key}
                  onClick={onClick}
                  className={`${base} ${tone}`}
                  data-testid={`booking-option-${o.key}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#F3EFE6] flex items-center justify-center group-hover:bg-[#FAF9F6] transition-colors">
                    <Icon className="w-5 h-5 text-[#1A362D]" strokeWidth={1.5} />
                  </div>
                  <div className="text-left">
                    <p className="font-display text-lg text-[#1A362D] group-hover:text-inherit">
                      {o.label}
                    </p>
                    <p className="text-xs text-[#5C4033] group-hover:text-current/80">
                      {o.desc}
                    </p>
                  </div>
                </button>
              );
            })}
            {options.length === 0 && (
              <p className="text-sm text-[#5C4033]">
                No booking channels currently enabled.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
